import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PreviewRenderer from '../components/PreviewRenderer';
import LanguageSelector, { getPreferredLanguage, SUPPORTED_LANGUAGES } from '../components/LanguageSelector';
import { AlertTriangle, Eye, RefreshCw } from 'lucide-react';
import PagePopupModal from '../components/PagePopupModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PreviewPage = () => {
  const { token } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Translation state
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translatedBlocks, setTranslatedBlocks] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/public/preview/${token}`);
        setPageData(response.data);
        setError(null);
        
        // Initialize language based on tenant settings
        const branding = response.data.branding || {};
        if (branding.translation_enabled) {
          const preferred = getPreferredLanguage();
          const defaultLang = branding.default_language || 'en';
          if (preferred !== defaultLang && SUPPORTED_LANGUAGES[preferred]) {
            setCurrentLanguage(preferred);
          } else {
            setCurrentLanguage(defaultLang);
          }
        }
      } catch (err) {
        console.error('Preview fetch error:', err);
        setError(err.response?.data?.detail || 'Preview not found or link has expired');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPreview();
    }
  }, [token]);
  
  // Fetch translation when language changes
  useEffect(() => {
    const fetchTranslation = async () => {
      const branding = pageData?.branding || {};
      const defaultLanguage = branding.default_language || 'en';
      
      if (!branding.translation_enabled || currentLanguage === defaultLanguage || !pageData?.page?.page_id) {
        setTranslatedBlocks(null);
        return;
      }
      
      const subdomain = pageData?.site?.subdomain || pageData?.site?.site_slug;
      if (!subdomain) {
        setTranslatedBlocks(null);
        return;
      }
      
      setIsTranslating(true);
      try {
        const response = await axios.get(
          `${API_URL}/api/public/${subdomain}/page/${pageData.page.slug}/translated/${currentLanguage}`
        );
        
        if (response.data.translated && response.data.page?.blocks) {
          setTranslatedBlocks(response.data.page.blocks);
          console.log(`[Translation] Preview translated to ${currentLanguage}`);
        }
      } catch (error) {
        console.error('[Translation] Error:', error);
        setTranslatedBlocks(null);
      } finally {
        setIsTranslating(false);
      }
    };
    
    if (pageData) {
      fetchTranslation();
    }
  }, [currentLanguage, pageData]);
  
  const handleLanguageChange = (newLang) => {
    setCurrentLanguage(newLang);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md px-4">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Preview Not Available</h1>
          <p className="text-slate-600 mb-4">{error}</p>
          <p className="text-sm text-slate-500">
            This preview link may have expired or been regenerated. 
            Please request a new preview link from the page owner.
          </p>
        </div>
      </div>
    );
  }

  if (!pageData) {
    return null;
  }

  const { page, site_theme, navigation, site, is_preview, tenant_id, all_pages, branding } = pageData;
  
  // Check if translation is enabled for this tenant
  const translationEnabled = branding?.translation_enabled === true;
  const defaultLanguage = branding?.default_language || 'en';
  
  // Get all pages for navigation link resolution
  const allPages = all_pages || pageData.pages || [];
  
  // Extract subdomain for navigation search
  const subdomain = site?.subdomain || site?.site_slug;
  
  // Determine page width class
  const getWidthClass = () => {
    switch (page.page_width) {
      case 'wide': return 'max-w-7xl';
      case 'full': return 'max-w-none';
      case 'narrow': return 'max-w-3xl';
      default: return 'max-w-5xl';
    }
  };
  
  // Determine which blocks to render (translated or original)
  const blocksToRender = translatedBlocks || page.content || [];

  return (
    <>
      {/* noindex meta tag for SEO protection */}
      <meta name="robots" content="noindex, nofollow" />
      
      {/* Language Selector - Only show if translation is enabled */}
      {translationEnabled && (
        <LanguageSelector
          currentLanguage={currentLanguage}
          onLanguageChange={handleLanguageChange}
          isTranslating={isTranslating}
          defaultLanguage={defaultLanguage}
        />
      )}
      
      <div className="min-h-screen bg-white">
        {/* Preview Banner */}
        {is_preview && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 text-center text-sm font-medium sticky top-0 z-50 shadow-md">
            <div className="flex items-center justify-center gap-2">
              <Eye size={16} />
              <span>
                Preview Mode — {page.status === 'draft' ? 'Draft' : 'Published'} Version
              </span>
              {page.status === 'draft' && (
                <span className="bg-white/20 px-2 py-0.5 rounded text-xs ml-2">
                  Not yet published
                </span>
              )}
              {translatedBlocks && (
                <span className="bg-white/20 px-2 py-0.5 rounded text-xs ml-2">
                  Translated to {SUPPORTED_LANGUAGES[currentLanguage]?.name || currentLanguage}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Page Content - No max-width constraint to allow full-width blocks */}
        <div className="w-full">
          {/* Menu picker mirrors public site: page-specific menus win over
              global at the same location; both are gated by `is_global`
              or the current page appearing in `page_ids`. Without this,
              /preview always showed the first menu at this location
              regardless of Navigation-Manager page assignments. */}
          {(() => {
            const pageId = page?.page_id || null;
            const pickMenu = (location) => {
              const candidates = (navigation || []).filter(m => m.location === location);
              const pageSpecific = candidates.find(m => pageId && m.page_ids?.includes(pageId));
              if (pageSpecific) return pageSpecific;
              return candidates.find(m => m.is_global) || null;
            };
            return (
              <PreviewRenderer
                blocks={blocksToRender}
                siteTheme={site_theme || {}}
                theme={page.theme}
                pageWidth={page.page_width}
                headerMenu={pickMenu('header')}
                footerMenu={pickMenu('footer')}
                isQuickPreview={false}
                showNavigation={true}
                previewToken={token}
                pages={allPages}
                tenantId={tenant_id}
                subdomain={subdomain}
              />
            );
          })()}
        </div>

        {/* Footer attribution */}
        <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-400">
          Preview generated by {site?.name || 'NexusCMS'}
        </div>
      </div>

      {/* Popups — show on preview so admins can proof them alongside the page */}
      <PagePopupModal tenantId={tenant_id} pageId={page?.page_id || null} />
    </>
  );
};

export default PreviewPage;
